import { useEffect, useRef, useState } from 'react'
import { ChevronDown, X } from 'lucide-react'
import { USER_SETTABLE_STATUSES } from '../../../netlify/functions/lib/bug-management/statuses'
import { useChangeBugStatusMutation } from '../../services/projectDataApi'
import StatusBadge from '../StatusBadge'
import './BugBulkActions.css'

interface BugBulkActionsProps {
  selectedIds: string[]
  onClear: () => void
}

// The batch-edit bar the Bugs tab shows above the table while rows are checked. Each action applies to
// every selected bug through the same per-bug PATCH the row menus use, so server-side side effects (e.g.
// a resolved bug retrying its journey) fire exactly as they would for a one-at-a-time change.
export default function BugBulkActions({ selectedIds, onClear }: BugBulkActionsProps) {
  const [changeBugStatus] = useChangeBugStatusMutation()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  // A new selection makes the last failure message stale.
  useEffect(() => { setError(null) }, [selectedIds.length])

  async function applyStatus(status: string) {
    setOpen(false)
    setBusy(true)
    setError(null)
    const results = await Promise.allSettled(selectedIds.map(id => changeBugStatus({ id, status }).unwrap()))
    setBusy(false)
    const failed = results.filter(r => r.status === 'rejected').length
    if (failed === 0) onClear()
    else setError(`${failed} of ${selectedIds.length} bugs could not be updated`)
  }

  const count = selectedIds.length
  return (
    <div className="bug-bulk-actions" role="toolbar" aria-label="Batch actions" data-testid="bug-bulk-actions">
      <span className="bug-bulk-actions__count" data-testid="bug-bulk-count">
        {count} selected
      </span>
      <div className="bug-bulk-actions__menu-wrap" ref={ref}>
        <button
          type="button"
          className="bug-bulk-actions__btn"
          onClick={() => setOpen(o => !o)}
          disabled={busy}
          aria-haspopup="menu"
          aria-expanded={open}
          data-testid="bug-bulk-status-trigger"
          data-analytics-event="bug-bulk-status-toggle"
        >
          {busy ? 'Updating…' : 'Change status'}
          {!busy && <ChevronDown size={14} aria-hidden="true" />}
        </button>
        {open && (
          <div className="bug-bulk-actions__menu" role="menu" aria-label={`Change status of ${count} bugs`} data-testid="bug-bulk-status-menu">
            <span className="bug-bulk-actions__menu-label">Set {count} {count === 1 ? 'bug' : 'bugs'} to</span>
            {USER_SETTABLE_STATUSES.map(s => (
              <button
                type="button"
                key={s}
                className="bug-bulk-actions__option"
                role="menuitem"
                onClick={() => void applyStatus(s)}
                data-testid={`bug-bulk-status-${s}`}
                data-analytics-event={`bug-bulk-status-${s}`}
              >
                <StatusBadge status={s} variant="bug" />
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <span className="bug-bulk-actions__error" role="alert" data-testid="bug-bulk-error">{error}</span>}
      <button
        type="button"
        className="bug-bulk-actions__clear"
        onClick={onClear}
        disabled={busy}
        data-testid="bug-bulk-clear"
        data-analytics-event="bug-bulk-clear"
      >
        <X size={14} aria-hidden="true" />
        Clear selection
      </button>
    </div>
  )
}
